import { useRef, useState, type ReactNode } from 'react'
import { Popover } from '@opallabs/lapidary'
import { type CreateCalendarItemParams } from '../useCreateCalendarItem'
import {
  CreateForm,
  type CreateChannelOption,
  type CreateStampOption,
} from './CreateForm'

export { selectors as calendarCreateSelectors } from './styles'
export type { CreateChannelOption, CreateStampOption }

export type CalendarCreatePopoverProps = Readonly<{
  channels: readonly CreateChannelOption[]
  contentStamps: readonly CreateStampOption[]
  date: Date
  dateLabel: string
  isSubmitting: boolean
  momentStamps: readonly CreateStampOption[]
  onChannelChange?: (channelId: string | null) => void
  onSubmit: (params: CreateCalendarItemParams) => Promise<unknown>
  /** The button that opens the form, drawn by whoever is asking for it. */
  trigger: ReactNode
}>

/**
 * The form for making something on a day, hung off whatever opened it.
 *
 * It closes itself once the thing is made, and stays open when the request
 * fails so the person still has what they typed.
 */
export const CalendarCreatePopover: React.FC<CalendarCreatePopoverProps> = ({
  channels,
  contentStamps,
  date,
  dateLabel,
  isSubmitting,
  momentStamps,
  onChannelChange,
  onSubmit,
  trigger,
}) => {
  const [isOpen, setOpen] = useState(false)
  const titleInput = useRef<HTMLInputElement>(null)

  return (
    <Popover.Root open={isOpen} onOpenChange={setOpen}>
      <Popover.Trigger>{trigger}</Popover.Trigger>
      <Popover.Content
        align="start"
        side="bottom"
        // Radix would give the first control the cursor, which is the
        // Content/Moment switch. The title is what a person came to type.
        onOpenAutoFocus={(event) => {
          event.preventDefault()
          titleInput.current?.focus()
        }}
      >
        <CreateForm
          channels={channels}
          contentStamps={contentStamps}
          date={date}
          dateLabel={dateLabel}
          isSubmitting={isSubmitting}
          momentStamps={momentStamps}
          titleInputRef={titleInput}
          onCancel={() => setOpen(false)}
          onChannelChange={onChannelChange}
          onSubmit={async (params) => {
            try {
              await onSubmit(params)
              setOpen(false)
            } catch {
              // The mutation says what went wrong. The form stays open, still
              // holding what was typed, so it can be tried again.
            }
          }}
        />
      </Popover.Content>
    </Popover.Root>
  )
}
CalendarCreatePopover.displayName = 'Calendar: CalendarCreatePopover'
